// Timestop BO — user-badge dropdown menu.
//
// Phase 12.10: the topbar's user badge (username pill on the right) is now
// a button that opens a small dropdown with three actions: Profile, Settings,
// Sign out. Sign-out is moved out of the topbar — the dropdown is now the
// only way to log out, which kills the accidental-click problem with the
// old standalone "Sign out" button.
//
// Two modals back the first two menu items:
//   - Profile: view-only account data (username / email / role / op_id) +
//              a self-serve "Change password" form (old + new + new2).
//   - Settings: language picker (EN / CZ / PL) persisted to localStorage
//              + mirrored to the admin_lang cookie (so admin/api/I18n.php
//              resolves the new locale on the very next request) + POSTed
//              to /api/change_my_lang (writes users.lang for cross-device
//              persistence). The static chrome (nav, topbar) is localized
//              by the PHP-side t() in admin/shell.php / admin/index.php
//              using admin/locales/{lang}.json; the .ini files in
//              admin/langs/ are the legacy Phase 12.18 prototype and are
//              no longer read.
//
// `Sign out` reuses the existing window.doLogout() — that wrapper already
// stops the dashboard ticker / log polling, calls /api/logout, wipes the
// shell, and reloads.

import { apiFetch, type ApiFetchConfig } from './api';
import type { TimestopState } from './state';
import { t } from './i18n';
import { subscribeToPush, unsubscribeFromPush, type PushState } from './push';

// =====================================================================
// Dropdown
// =====================================================================

function isOpen(): boolean {
    const menu = document.getElementById('user-menu');
    return !!menu && !menu.classList.contains('hidden');
}

export function toggleUserMenu(): void {
    if (isOpen()) {
        closeUserMenu();
    } else {
        openUserMenu();
    }
}

export function openUserMenu(): void {
    const menu = document.getElementById('user-menu');
    const btn = document.getElementById('user-badge-btn');
    if (!menu || !btn) return;
    // Re-paint the PWA install item visibility on every open. The
    // bundle's `bindGlobalListeners` subscribes to the inline
    // script's change event, but if the user opens the dropdown
    // before the SW finishes activating (or before the
    // beforeinstallprompt event has fired), the item needs a
    // fresh re-check. Idempotent — the helper is a no-op if the
    // state hasn't changed.
    refreshPwaInstallVisibility();
    menu.classList.remove('hidden');
    btn.setAttribute('aria-expanded', 'true');
}

export function closeUserMenu(): void {
    const menu = document.getElementById('user-menu');
    const btn = document.getElementById('user-badge-btn');
    if (!menu || !btn) return;
    menu.classList.add('hidden');
    btn.setAttribute('aria-expanded', 'false');
}

// Show or hide the "Install Timestop" menu item based on the inline
// script's `timestopPwa` state. The item is rendered in shell.php with
// the .hidden Tailwind class so it never flashes on a host without
// PWA support; we just toggle the class off when the browser has
// captured an install prompt AND the app isn't already running as an
// installed PWA. Idempotent — safe to call on every change.
function refreshPwaInstallVisibility(): void {
    const item = document.querySelector<HTMLElement>('[data-pwa-install]');
    if (!item) return;
    const w = window as unknown as {
        timestopPwa?: { canInstall?: boolean; installed?: boolean };
    };
    const pwa = w.timestopPwa;
    const show = !!(pwa && pwa.canInstall && !pwa.installed);
    item.classList.toggle('hidden', !show);
}

// Close on outside click + Escape. Wired up once on first use of the
// dropdown; idempotent — re-binding is a no-op because the listener is
// the same function reference.
let _globalListenersBound = false;
function bindGlobalListeners(): void {
    if (_globalListenersBound) return;
    _globalListenersBound = true;
    document.addEventListener('click', (ev) => {
        if (!isOpen()) return;
        const menu = document.getElementById('user-menu');
        const btn = document.getElementById('user-badge-btn');
        const target = ev.target as Node | null;
        if (!target) return;
        if (menu && menu.contains(target)) return;
        if (btn && btn.contains(target)) return;
        closeUserMenu();
    });
    document.addEventListener('keydown', (ev) => {
        if (ev.key === 'Escape' && isOpen()) {
            closeUserMenu();
        }
    });
    // PWA: subscribe to the inline script's install-prompt changes so
    // the "Install" item in the dropdown is shown the moment the
    // browser fires beforeinstallprompt, and hidden again on
    // appinstalled / dismissal. We also re-paint once at boot so
    // the item appears even if the event fired before this
    // listener attached (e.g. on a slow Vite cold start).
    refreshPwaInstallVisibility();
    const w = window as unknown as {
        timestopPwa?: { onChange?: (cb: (s: unknown) => void) => () => void };
    };
    if (typeof w.timestopPwa?.onChange === 'function') {
        w.timestopPwa.onChange(() => refreshPwaInstallVisibility());
    }
}

// Eagerly bind the global listeners at module init. Without this,
// the PWA install item only becomes visible AFTER the user opens
// the dropdown once (because bindGlobalListeners is also the
// function that wires up refreshPwaInstallVisibility). Since the
// PWA capture happens at page load (inline script), the bundle
// can know at first paint whether the prompt is available and
// pre-paint the menu item correctly.
bindGlobalListeners();

// Click delegation for the three menu items. Called from HTML onclick
// attributes (`data-user-menu="profile" | "settings" | "signout" |
// "install"`).
export function handleUserMenuClick(action: string, state: TimestopState, config: ApiFetchConfig): void {
    bindGlobalListeners();
    closeUserMenu();
    if (action === 'profile') {
        openProfileModal(state, config);
    } else if (action === 'settings') {
        // Forward the state + config so the picker's seed selection
        // can prefer state.currentUser.lang (DB) over localStorage,
        // AND the click handler in openSettingsModal can call the
        // /api/change_my_lang endpoint. Without passing config the
        // click falls through to the localStorage-only legacy path
        // — see openSettingsModal() comment for the rationale.
        openSettingsModal(state, config);
    } else if (action === 'install') {
        // PWA install: the user-menu button is shown by
        // refreshPwaInstallVisibility() when the browser has
        // captured a `beforeinstallprompt` event. The click fires
        // the browser's native install sheet via the inline script
        // in admin/index.php (window.timestopPwa.prompt). We don't
        // need state or config for this path.
        const w = window as unknown as {
            timestopPwa?: { prompt?: () => Promise<{ ok: boolean; outcome?: string; reason?: string }> };
        };
        if (typeof w.timestopPwa?.prompt === 'function') {
            void w.timestopPwa.prompt();
        } else {
            // Fallback for hosts where the inline script didn't
            // load (e.g. an admin page without the PWA wiring).
            // Best-effort: surface a hint and let the user know
            // the install UX is unavailable.
            console.warn('[timestop] window.timestopPwa.prompt missing — install unavailable');
        }
    } else if (action === 'signout') {
        // The bundle's doLogout on window takes (state, config,
        // apiFetchFn, hooks). The inline scripts in index.php
        // expose `window.apiFetchCore` and `window.clearAppShell`
        // for the hooks; we just thread them through here. Doing
        // this directly is the right call — calling
        // `window.doLogout()` with no args used to recurse into the
        // inline wrapper (which itself reads window.doLogout), and
        // the inline wrapper stops the dashboard ticker / log
        // polling before calling the bundle, which we still want.
        const w = window as unknown as {
            doLogout?: (s: TimestopState, c: ApiFetchConfig, fn: typeof apiFetch, hooks: { clearAppShell: () => void }) => Promise<void> | void;
        };
        if (typeof w.doLogout !== 'function') {
            console.warn('[timestop] window.doLogout missing — bundle not loaded?');
            return;
        }
        void w.doLogout(
            state,
            config,
            apiFetch,
            { clearAppShell: (window as unknown as { clearAppShell?: () => void }).clearAppShell || (() => {}) },
        );
    }
}

// =====================================================================
// Profile modal
// =====================================================================

function esc(s: string): string {
    return s
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

function setText(id: string, value: string): void {
    const el = document.getElementById(id);
    if (el) el.textContent = value;
}

export function openProfileModal(state: TimestopState, config?: ApiFetchConfig): void {
    const modal = document.getElementById('profile-modal');
    if (!modal) return;
    // Defensive: clear any stale "loading" flag from a prior hung
    // subscribe() call. If the user closed/reopened the profile
    // modal while a previous pushManager.subscribe() was still
    // pending (e.g. Firefox hangs on the request, or the user
    // closed the browser tab mid-request), the loading flag
    // would otherwise stay true and the next click would hit
    // the 'already_loading' early-return in push.ts. The next
    // subscribe() call resets it to true at the start of its
    // try block, so a successful openProfileModal + click will
    // retry normally.
    const pwa = (state as unknown as { pwa?: { loading?: boolean; permission?: string; subscribed?: boolean } }).pwa;
    if (pwa) {
        pwa.loading = false;
    }
    // Wire the Subscribe/Unsubscribe click handler once on the
    // first open. We need config (for the apiFetch base +
    // CSRF) so the closure captures it here. The handleUserMenuClick
    // caller always passes config, so the closure is non-null
    // in production. The optional chain guards the rare case
    // where openProfileModal is called from a test harness
    // without config — the buttons will be no-ops in that case
    // (the click handler falls through without firing the
    // network call).
    if (config) bindPushClickOnce(state, config);
    const u = state.currentUser;
    const username = u?.username || '?';
    const email = (u && (u as { email?: string }).email) || '';
    const role = (u?.role as string) || 'guest';
    const opId = (u && (u as { op_id?: number | string | null }).op_id);
    const initial = username.slice(0, 2).toUpperCase();

    setText('profile-initial', initial);
    setText('profile-username', username);
    setText('profile-role', role[0]?.toUpperCase() + role.slice(1));
    setText('profile-username-row', username);
    setText('profile-email', email || '–');
    setText('profile-role-row', role[0]?.toUpperCase() + role.slice(1));
    // Operator group: show the resolved name (e.g. "TEST_TEST") rather
    // than the numeric op_id. The list of groups is populated by
    // /api/data on dashboard load and lives on state.groups. We do a
    // plain linear lookup because the list is tiny (single-digit rows
    // in production) and matching is by stringified id to mirror how
    // the rest of the renderers key into state.groups. "Unassigned" is
    // shown when the user has no op_id (admin, or operator with no
    // group binding); "Unknown (N)" is a defensive fallback for a
    // stale op_id that no longer exists in oper.
    let opLabel = t('common.unassigned');
    if (opId != null && opId !== 0 && opId !== '') {
        const oidStr = String(opId);
        const grp = state.groups.find((g) => String(g.id) === oidStr);
        if (grp && grp.name) {
            opLabel = String(grp.name);
        } else {
            opLabel = t('user_menu.profile_unknown', { id: oidStr });
        }
    }
    setText('profile-oid', opLabel);

    // Web Push (Phase 14 / docs/pwa-plan.md §3): render the
    // notification section based on state.pwa. Three sub-blocks
    // are in admin/index.php's profile-modal; we toggle which
    // is visible, the button text, and the status line.
    paintPushSection(state);

    // Reset the form + any leftover message
    const form = document.getElementById('change-my-password-form') as HTMLFormElement | null;
    if (form) form.reset();
    const msg = document.getElementById('change-my-password-msg');
    if (msg) {
        msg.textContent = '';
        msg.className = 'text-xs mb-2 min-h-[1em]';
    }

    modal.classList.remove('hidden');
}

export function closeProfileModal(): void {
    const modal = document.getElementById('profile-modal');
    if (modal) modal.classList.add('hidden');
}

// =====================================================================
// Profile modal — Web Push (Phase 14 / docs/pwa-plan.md §3)
// =====================================================================
// Three render paths based on state.pwa (see push.ts):
//   1. canSubscribe === false  -> show "unsupported" hint,
//      no buttons. (Browser lacks PushManager or Notification.)
//   2. permission === 'denied' -> show amber "blocked" hint,
//      no buttons. The user has to flip the site permission in
//      their browser settings; we can't re-prompt from JS.
//   3. otherwise              -> show the Subscribe / Unsubscribe
//      buttons. pwa.subscribed toggles which one is visible.
//
// The click handler is wired once (idempotent) on first open of
// the modal; subsequent opens just re-paint. The handler
// dispatches to subscribeToPush() / unsubscribeFromPush() and
// re-paints when the result lands.

function paintPushSection(state: TimestopState): void {
    const unsupported = document.getElementById('profile-push-unsupported');
    const denied = document.getElementById('profile-push-denied');
    const ready = document.getElementById('profile-push-ready');
    const sub = document.getElementById('profile-push-subscribe');
    const unsub = document.getElementById('profile-push-unsubscribe');
    if (!unsupported || !denied || !ready || !sub || !unsub) return;
    const pwa = state.pwa as PushState | undefined;
    // Default: everything hidden. The three render paths show one.
    unsupported.classList.add('hidden');
    denied.classList.add('hidden');
    ready.classList.add('hidden');
    if (!pwa) {
        // ensurePushReady hasn't run yet -- treat as "loading"
        // (a transient state that should self-resolve). Showing
        // the unsupported hint here would be misleading.
        return;
    }
    if (pwa.canSubscribe === false) {
        unsupported.classList.remove('hidden');
        return;
    }
    if (pwa.permission === 'denied') {
        denied.classList.remove('hidden');
        return;
    }
    // Ready state. Show the right button based on pwa.subscribed.
    ready.classList.remove('hidden');
    if (pwa.subscribed) {
        sub.classList.add('hidden');
        unsub.classList.remove('hidden');
    } else {
        sub.classList.remove('hidden');
        unsub.classList.add('hidden');
    }
    // Disable both buttons while a request is in flight.
    const disabled = !!pwa.loading;
    (sub as HTMLButtonElement).disabled = disabled;
    (unsub as HTMLButtonElement).disabled = disabled;

    // Show the running service worker version. The SW replies via
    // postMessage when it receives a 'timestop:sw_version' ping. We
    // check navigator.serviceWorker.getRegistration() (NOT
    // .controller) because Chrome resets the controller on
    // hard-reload -- the SW is still installed and active, but
    // the page that just hard-reloaded doesn't have a controller
    // until the next navigation. The registration persists
    // across hard-reloads. If there's a registration, we
    // postMessage its `active` worker (or `waiting` if active
    // isn't ready yet). The 1s timeout fallback shows
    // "older version" if the running SW predates the message
    // handler.
    const swLabel = document.getElementById('profile-push-sw-version');
    if (swLabel) {
        const sw = navigator.serviceWorker;
        if (!sw || !sw.getRegistration) {
            swLabel.textContent = t('user_menu.push_sw_none');
            swLabel.classList.remove('hidden');
        } else {
            sw.getRegistration().then((reg) => {
                if (!reg) {
                    swLabel.textContent = t('user_menu.push_sw_none');
                    swLabel.classList.remove('hidden');
                    return;
                }
                // Wait for the SW to be active. ready resolves
                // when the SW for this page is controlling.
                const worker = reg.active || reg.waiting || reg.installing;
                if (!worker) {
                    swLabel.textContent = t('user_menu.push_sw_none');
                    swLabel.classList.remove('hidden');
                    return;
                }
                swLabel.textContent = t('user_menu.push_sw_querying');
                swLabel.classList.remove('hidden');
                let replied = false;
                try {
                    const ch = new MessageChannel();
                    ch.port1.onmessage = (mev) => {
                        const d = mev.data as { type?: string; version?: string; cache?: string } | null;
                        if (d && d.type === 'timestop:sw_version' && d.version) {
                            replied = true;
                            swLabel.textContent = t('user_menu.push_sw_running', { version: d.version });
                        }
                    };
                    worker.postMessage({ type: 'timestop:sw_version' }, [ch.port2]);
                } catch (e) {
                    swLabel.textContent = t('user_menu.push_sw_none');
                }
                // 1s timeout fallback: if the running SW predates
                // the version-ping handler, it won't reply. Don't
                // keep the user on "checking…" -- tell them the
                // SW is older.
                window.setTimeout(() => {
                    if (!replied) {
                        swLabel.textContent = t('user_menu.push_sw_older');
                    }
                }, 1000);
            }).catch(() => {
                swLabel.textContent = t('user_menu.push_sw_none');
                swLabel.classList.remove('hidden');
            });
        }
    }
}

let _pushClickBound = false;
function bindPushClickOnce(state: TimestopState, config: ApiFetchConfig): void {
    if (_pushClickBound) return;
    _pushClickBound = true;
    const handler = async (ev: Event) => {
        const target = (ev.target as HTMLElement | null)?.closest<HTMLElement>('[data-push-action]');
        if (!target) return;
        const action = target.getAttribute('data-push-action');
        if (action === 'subscribe') {
            const r = await subscribeToPush(state, config);
            setPushMsg(r.ok ? t('user_menu.push_subscribed_ok') : errorKeyForResult(r.error || 'unknown'));
        } else if (action === 'unsubscribe') {
            const r = await unsubscribeFromPush(state, config);
            setPushMsg(r.ok ? t('user_menu.push_unsubscribed_ok') : errorKeyForResult(r.error || 'unknown'));
        }
        paintPushSection(state);
    };
    // Bind on document so the click works whether the button is
    // in the open modal (currently) or in a future "open from
    // anywhere" location.
    //
    // CRITICAL: must use capture phase (third arg = true). The
    // three modals (profile, avatar-crop, settings) wrap their
    // content in a <div onclick="event.stopImmediatePropagation()">
    // to keep the modal open when you click inside it (otherwise
    // the modal-close-on-outside-click handler would fire). That
    // stopImmediatePropagation runs in the bubble phase and would
    // kill our handler if we listened in bubble phase. Capture
    // phase runs first (top-down, document -> target) so we see
    // every click on a push button before stopImmediatePropagation
    // gets a chance.
    document.addEventListener('click', handler, true);
}

function errorKeyForResult(code: string): string {
    // Map the structured error codes from push.ts into i18n
    // keys. The keys live in admin/locales/{en,cz,pl}.json
    // under user_menu.push_err_*.
    switch (code) {
        case 'permission_denied':
            return t('user_menu.push_err_permission_denied');
        case 'permission_default':
            return t('user_menu.push_err_permission_default');
        case 'vapid_key_unavailable':
        case 'push_not_configured':
            return t('user_menu.push_err_vapid_unavailable');
        case 'push_not_supported':
            return t('user_menu.push_err_unsupported');
        case 'push_subscribe_timeout':
            return t('user_menu.push_err_timeout');
        case 'service_worker_not_ready':
            return t('user_menu.push_err_sw_not_ready');
        default:
            return t('user_menu.push_err_generic', { code });
    }
}

function setPushMsg(text: string, kind: 'ok' | 'err' | 'info' = 'info'): void {
    const m = document.getElementById('profile-push-msg');
    if (!m) return;
    m.textContent = text;
    m.className = 'text-xs mt-2 min-h-[1em] ' +
        (kind === 'ok'  ? 'text-emerald-600'
        : kind === 'err' ? 'text-red-600'
        :                  'text-gray-500');
}

export async function submitChangeMyPassword(ev: Event, state: TimestopState, config: ApiFetchConfig): Promise<boolean> {
    ev.preventDefault();
    const form = ev.target as HTMLFormElement | null;
    const msg = document.getElementById('change-my-password-msg');
    const submit = document.getElementById('change-my-password-submit') as HTMLButtonElement | null;
    if (!form || !msg || !submit) return false;

    const oldPass = (form.elements.namedItem('old')  as HTMLInputElement | null)?.value ?? '';
    const newPass = (form.elements.namedItem('new')  as HTMLInputElement | null)?.value ?? '';
    const newPass2 = (form.elements.namedItem('new2') as HTMLInputElement | null)?.value ?? '';

    function setMsg(text: string, kind: 'err' | 'ok' | 'info'): void {
        if (!msg) return;
        msg.textContent = text;
        msg.className = 'text-xs mb-2 min-h-[1em] ' +
            (kind === 'err' ? 'text-red-600'
              : kind === 'ok'  ? 'text-emerald-600'
              : 'text-gray-500');
    }

    if (!oldPass) { setMsg(t('user_menu.current_password_required'), 'err'); return false; }
    if (newPass.length < 6) { setMsg(t('user_menu.password_too_short'), 'err'); return false; }
    if (newPass !== newPass2) { setMsg(t('user_menu.password_mismatch'), 'err'); return false; }
    if (oldPass === newPass) { setMsg(t('user_menu.password_same_as_old'), 'err'); return false; }

    submit.disabled = true;
    setMsg(t('user_menu.password_changing'), 'info');
    try {
        const r = await apiFetch('change_my_password', {
            method: 'POST',
            body: { old: oldPass, new: newPass },
        }, config);
        if (!r.ok) {
            const errCode = ((r.data as { error?: string } | null)?.error) || 'change_failed';
            const errMsg = errCode === 'wrong_old_password'
                ? t('user_menu.password_wrong')
                : errCode === 'weak_password'
                    ? t('user_menu.password_weak')
                    : errCode === 'no_session'
                        ? t('errors.session_expired')
                        : t('user_menu.password_failed', { code: errCode });
            setMsg(errMsg, 'err');
            return false;
        }
        setMsg(t('user_menu.password_changed'), 'ok');
        form.reset();
        return false;
    } catch (e) {
        setMsg(t('user_menu.password_failed', { code: (e as Error).message }), 'err');
        return false;
    } finally {
        submit.disabled = false;
    }
}

// =====================================================================
// Settings modal (language picker)
// =====================================================================
//
// Phase 12.10: language picker persisted to localStorage only. The DB
// column (users.lang) existed but the SPA never wrote to it.
//
// Phase 12.18: now persists to the server. Click handler in
// openSettingsModal() POSTs {lang} to /api/change_my_lang, and:
//   - On 2xx: updates state.currentUser.lang (single source of truth
//     for the topbar/Profile) and mirrors the value to localStorage
//     so the picker feels instant on reopen and a "remember me" hint
//     survives a logout/login round-trip even before /api/me re-issues.
//   - On 4xx/5xx: keeps the localStorage mirror (the user can still
//     pick) but logs the error so the operator can see persistence
//     failed. We do NOT roll back the UI — the server is the
//     eventual truth and a transient 5xx should self-heal on the
//     next click.
//
// `currentLang()` priority for the seed selection on open:
//   1. state.currentUser.lang   (DB, most recent server write)
//   2. localStorage.timestop.lang (legacy / cross-device carry-over)
//   3. 'en'                     (DB column default)

const LANG_KEY = 'timestop.lang';
const SUPPORTED_LANGS = ['en', 'cz', 'pl'] as const;
type Lang = (typeof SUPPORTED_LANGS)[number];

function isLang(v: unknown): v is Lang {
    return typeof v === 'string' && (SUPPORTED_LANGS as readonly string[]).includes(v);
}

function readLocalLang(): Lang | null {
    try {
        const v = localStorage.getItem(LANG_KEY);
        if (isLang(v)) return v;
    } catch { /* localStorage may be disabled */ }
    return null;
}

function writeLocalLang(lang: Lang): void {
    try { localStorage.setItem(LANG_KEY, lang); } catch { /* ignore */ }
    // Mirror to the admin_lang cookie so admin/api/I18n.php's
    // t_active_lang() resolves the new locale on the very next
    // request -- without this, PHP only sees the new value after
    // the /api/change_my_lang POST has written to users.lang AND
    // the request handler has re-read it, which is a race window
    // where the static nav (rendered by PHP-side t()) stays in
    // the old language until a full reload. Path=/ + SameSite=Lax
    // so the cookie is sent on all same-origin navigation. No
    // max-age -> session cookie, matching the original spec in
    // admin/api/I18n.php ("a hostile cookie can't inject an
    // arbitrary file path" is enforced by the I18N_LANGS whitelist
    // on the server side).
    try {
        document.cookie = 'admin_lang=' + encodeURIComponent(lang)
            + '; path=/; SameSite=Lax';
    } catch { /* ignore */ }
}

function currentLang(state?: TimestopState | null): Lang {
    if (state && isLang(state.currentUser?.lang)) return state.currentUser!.lang as Lang;
    const ls = readLocalLang();
    if (ls) return ls;
    return 'en';
}

function paintLangPicker(selected: Lang): void {
    const buttons = document.querySelectorAll<HTMLButtonElement>('#lang-picker .lang-option');
    buttons.forEach((btn) => {
        const lang = btn.getAttribute('data-lang');
        if (lang === selected) {
            btn.setAttribute('aria-checked', 'true');
            btn.classList.add('border-emerald-500', 'text-emerald-700', 'bg-emerald-50');
            btn.classList.remove('text-gray-700');
        } else {
            btn.setAttribute('aria-checked', 'false');
            btn.classList.remove('border-emerald-500', 'text-emerald-700', 'bg-emerald-50');
            btn.classList.add('text-gray-700');
        }
    });
}
/**
 * setLanguage — the canonical write path for the user's language
 * preference. Updates:
 *   1. localStorage ('timestop.lang')
 *   2. state.currentUser.lang
 *   3. The active in-memory catalog (setActiveLang) so the SPA
 *      re-paints in the new locale.
 *   4. The lang picker UI (paintLangPicker)
 *   5. The DB column users.lang via POST /api/change_my_lang
 *
 * Server failure is logged but does not block the UI -- the
 * localStorage mirror still holds the user's choice for next time.
 */
export async function setLanguage(
    lang: Lang,
    state: TimestopState,
    config: ApiFetchConfig,
): Promise<{ ok: boolean; error?: string }> {
    if (!isLang(lang)) return { ok: false, error: 'invalid_lang' };

    // Always update local state + localStorage first so the picker
    // reflects the click immediately.
    writeLocalLang(lang);
    if (state.currentUser) {
        state.currentUser.lang = lang;
    }
    paintLangPicker(lang);
    // Flip the in-memory catalog and trigger the re-render. The
    // main.ts onLangChange subscriber handles the page+modal
    // repaint; this is what makes the picker click "instant" on
    // the dynamic parts of the UI (the static shell is already
    // localized via the PHP-side t()).
    setActiveLangFromI18n(lang);

    try {
        const r = await apiFetch('change_my_lang', {
            method: 'POST',
            body: { lang },
        }, config);
        if (!r.ok) {
            const errCode: string =
                (r.data && typeof (r.data as { error?: unknown }).error === 'string')
                    ? (r.data as { error: string }).error
                    : 'save_failed';
            console.warn('[timestop] change_my_lang failed:', errCode, r.status);
            // Don't roll the UI back — the localStorage mirror still
            // holds the user's choice for next time.
            return { ok: false, error: errCode };
        }
        // Full reload so the PHP-rendered static chrome (nav, topbar,
        // login modal labels) re-paints in the new locale. The SPA
        // already re-rendered the dynamic parts above via
        // setActiveLangFromI18n(), but anything the server rendered
        // with t() in shell.php / index.php stays in the old language
        // until the document is reloaded -- the cookie mirror alone
        // helps the NEXT request, not the current paint. Reloading
        // here is the only way to get a single consistent locale across
        // static + dynamic in one click. Skipped on the error path
        // above so a failed save doesn't blow away the user's pick.
        window.location.reload();
        return { ok: true };
    } catch (e) {
        console.warn('[timestop] change_my_lang network error:', (e as Error).message);
        return { ok: false, error: 'network_error' };
    }
}

/**
 * Wrapper around i18n.setActiveLang that keeps the i18n module
 * optional: if a future build path doesn't bundle it (e.g. a
 * lightweight admin page), setLanguage still works for the
 * localStorage + DB writes; the page just stays in its current
 * language. Stage 1 chrome is already localized by PHP, so the
 * UX is acceptable even without the TS-side re-render.
 */
function setActiveLangFromI18n(lang: Lang): void {
    try {
        // The i18n module publishes its setActiveLang on window
        // for the same reason the other bundle functions are
        // bridged: the inline scripts and any out-of-bundle
        // callers (like this one) need it without a direct
        // import. The `Bundle` suffix avoids colliding with
        // potential future window.setActiveLang symbols.
        const w = window as unknown as { setActiveLangBundle?: (l: string) => void };
        if (typeof w.setActiveLangBundle === 'function') {
            w.setActiveLangBundle(lang);
        }
    } catch (e) {
        // Never let the i18n bridge break the lang write path.
        console.warn('[timestop] setActiveLang bridge failed:', e);
    }
}

export function openSettingsModal(state?: TimestopState | null, config?: ApiFetchConfig | null): void {
    const modal = document.getElementById('settings-modal');
    if (!modal) return;
    const seed = currentLang(state);
    paintLangPicker(seed);
    modal.classList.remove('hidden');
    // Wire up clicks once on first open. Re-binding is a no-op because
    // the function reference is stable.
    const picker = document.getElementById('lang-picker');
    if (picker && !picker.hasAttribute('data-bound')) {
        picker.setAttribute('data-bound', '1');
        // Capture the call-site state/config in this closure so the
        // click handler can write through /api/change_my_lang. The
        // resolved refs win over the live window.timestop lookups so
        // a call that passed an explicit state still mutates that
        // state object (and the rest of the SPA sees the change
        // because window.timestop.state IS the same object the
        // bootstrap seeded).
        const st = state ?? null;
        const cfg = config ?? null;
        picker.addEventListener('click', (ev) => {
            const target = (ev.target as HTMLElement | null)?.closest<HTMLButtonElement>('.lang-option');
            if (!target) return;
            const lang = target.getAttribute('data-lang');
            if (!lang || !isLang(lang)) return;
            // Fallback resolution order: explicit call-site args,
            // then window.timestop.* globals. The latter lets the
            // modal be opened from a unit test that didn't bother
            // passing a state/config — production always passes
            // both via handleUserMenuClick('settings', state, config).
            const w = window as unknown as { timestop?: { state?: TimestopState; config?: ApiFetchConfig } };
            const stEff = st ?? w.timestop?.state ?? null;
            const cfgEff = cfg ?? w.timestop?.config ?? null;
            if (!stEff || !cfgEff) {
                // No state wired up yet — fall back to the legacy
                // localStorage-only path. This should not happen in
                // production; the bundle always has a state by the
                // time the user can open the modal.
                writeLocalLang(lang);
                paintLangPicker(lang);
                return;
            }
            void setLanguage(lang, stEff, cfgEff);
        });
    }
}

export function closeSettingsModal(): void {
    const modal = document.getElementById('settings-modal');
    if (modal) modal.classList.add('hidden');
}
